import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { SITE_CONTENT } from "../content";
import Header from "../components/Header";
import Footer from "../components/Footer";

const tagColors = {
  "Food Culture": { bg: "#f5c518", color: "#1a1a1a" },
  "Our Values": { bg: "#006400", color: "#fff" },
  "Expansion News": { bg: "#cd3a27", color: "#fff" },
};

const BodyBlock = ({ block }) => {
  switch (block.type) {
    case "intro":
      return (
        <p
          style={{
            fontSize: "18px",
            lineHeight: 1.85,
            color: "#1a1a1a",
            fontWeight: 500,
            fontFamily: "Raleway, sans-serif",
            borderLeft: "4px solid #f5c518",
            paddingLeft: "20px",
            marginBottom: "2rem",
          }}
        >
          {block.text}
        </p>
      );

    case "heading":
      return (
        <h2
          style={{
            fontFamily: "Raleway, sans-serif",
            fontSize: "clamp(1.3rem, 3vw, 1.7rem)",
            fontWeight: 800,
            color: "#1a1a1a",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            marginBottom: "1rem",
            marginTop: "2.5rem",
            paddingBottom: "10px",
            borderBottom: "2px solid #f5c518",
          }}
        >
          {block.text}
        </h2>
      );

    case "paragraph":
      return (
        <p
          style={{
            fontSize: "16px",
            lineHeight: 1.9,
            color: "#333",
            marginBottom: "1.4rem",
            fontFamily: "Lato, sans-serif",
          }}
        >
          {block.text}
        </p>
      );

    case "bullets":
      return (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: "0 0 1.8rem 0",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {block.items.map((item, i) => (
            <li
              key={i}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                fontSize: "15px",
                color: "#333",
                lineHeight: 1.7,
                fontFamily: "Lato, sans-serif",
              }}
            >
              <span
                style={{
                  color: "#cd3a27",
                  fontWeight: 700,
                  fontSize: "18px",
                  flexShrink: 0,
                  lineHeight: 1.4,
                }}
              >
                ▶
              </span>
              {item}
            </li>
          ))}
        </ul>
      );

    case "cta":
      return (
        <div
          style={{
            background: "#0d0d0d",
            borderRadius: "6px",
            padding: "28px 32px",
            marginTop: "2.5rem",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            textAlign: "center",
            border: "2px solid #f5c518",
          }}
        >
          <p
            style={{
              fontFamily: "Raleway, sans-serif",
              fontSize: "17px",
              fontWeight: 700,
              color: "#fff",
              letterSpacing: "0.5px",
            }}
          >
            {block.text}
          </p>
          <a
            href={block.href}
            target="_blank"
            rel="noreferrer"
            style={{
              display: "inline-block",
              background: "#cd3a27",
              color: "#fff",
              fontFamily: "Raleway, sans-serif",
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "2px",
              textTransform: "uppercase",
              padding: "13px 32px",
              borderRadius: "3px",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.background = "#b02f1e")}
            onMouseLeave={(e) => (e.target.style.background = "#cd3a27")}
          >
            Order on Zomato 🍴
          </a>
        </div>
      );

    default:
      return null;
  }
};

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const posts = SITE_CONTENT.sections.blog.posts;
  const post = posts.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="rest-page">
        <Header />
        <main
          style={{
            minHeight: "70vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: "16px",
            paddingTop: "120px",
          }}
        >
          <h1
            style={{
              fontFamily: "Raleway, sans-serif",
              fontSize: "2rem",
              color: "#1a1a1a",
            }}
          >
            Blog post not found
          </h1>
          <Link
            to="/#blog"
            style={{
              color: "#cd3a27",
              fontFamily: "Raleway, sans-serif",
              fontWeight: 700,
              fontSize: "14px",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            ← Back to Blog
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const tagStyle = tagColors[post.tag] || { bg: "#555", color: "#fff" };
  const otherPosts = posts.filter((p) => p.id !== post.id);

  return (
    <div className="rest-page">
      <Header />

      <main
        // style={{ paddingTop: "110px", minHeight: "100vh" }}
        className="md:pt-16 pt-10"
      >
        {/* ── Hero Banner ── */}
        <div
          style={{
            position: "relative",
            padding: "100px 5vw 60px",
            overflow: "hidden",
            minHeight: "340px",
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          {/* Cover image background */}
          <img
            src={post.coverImage}
            alt={post.title}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: "block",
            }}
          />
          {/* Strong dark gradient overlay */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.65) 60%, rgba(0,0,0,0.4) 100%)",
            }}
          />

          <div
            style={{
              maxWidth: "800px",
              margin: "0 auto",
              position: "relative",
              zIndex: 1,
            }}
          >
            {/* Breadcrumb */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "24px",
                fontFamily: "Raleway, sans-serif",
                fontSize: "11px",
                letterSpacing: "1.5px",
                textTransform: "uppercase",
              }}
            >
              <Link
                to="/"
                style={{
                  color: "rgba(255,255,255,0.45)",
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => (e.target.style.color = "#f5c518")}
                onMouseLeave={(e) =>
                  (e.target.style.color = "rgba(255,255,255,0.45)")
                }
              >
                Home
              </Link>
              <span style={{ color: "rgba(255,255,255,0.25)" }}>›</span>
              <Link
                to="/#blog"
                style={{
                  color: "rgba(255,255,255,0.45)",
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => (e.target.style.color = "#f5c518")}
                onMouseLeave={(e) =>
                  (e.target.style.color = "rgba(255,255,255,0.45)")
                }
              >
                Blog
              </Link>
              <span style={{ color: "rgba(255,255,255,0.25)" }}>›</span>
              <span style={{ color: "#f5c518" }}>{post.tag}</span>
            </div>

            {/* Tag */}
            <span
              style={{
                display: "inline-block",
                background: tagStyle.bg,
                color: tagStyle.color,
                fontSize: "9px",
                fontWeight: 700,
                letterSpacing: "2.5px",
                textTransform: "uppercase",
                padding: "5px 12px",
                borderRadius: "3px",
                marginBottom: "20px",
              }}
            >
              {post.tag}
            </span>

            {/* Title */}
            <h1
              style={{
                fontFamily: "Raleway, sans-serif",
                fontSize: "clamp(1.8rem, 4.5vw, 3rem)",
                fontWeight: 800,
                color: "#fff",
                lineHeight: 1.2,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                marginBottom: "28px",
              }}
            >
              {post.title}
            </h1>

            {/* Meta row */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "20px",
                fontFamily: "Raleway, sans-serif",
                fontSize: "11px",
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.5)",
              }}
            >
              <span>📅 {post.date}</span>
              <span
                style={{
                  color: "rgba(255,255,255,0.2)",
                }}
              >
                |
              </span>
              <span>✍️ {post.author}</span>
              <span style={{ color: "rgba(255,255,255,0.2)" }}>|</span>
              <span>⏱ {post.readTime}</span>
            </div>
          </div>
        </div>

        {/* ── Content + Sidebar ── */}
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            padding: "64px 5vw",
            display: "grid",
            gridTemplateColumns: "1fr 320px",
            gap: "60px",
            alignItems: "start",
          }}
          className="blog-detail-grid"
        >
          {/* MAIN ARTICLE */}
          <article>
            {post.body.map((block, i) => (
              <BodyBlock key={i} block={block} />
            ))}

            {/* Back link */}
            <div
              style={{
                marginTop: "3rem",
                paddingTop: "2rem",
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <Link
                to="/#blog"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  fontFamily: "Raleway, sans-serif",
                  fontSize: "12px",
                  fontWeight: 700,
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  color: "#cd3a27",
                  transition: "gap 0.2s",
                }}
              >
                ← Back to All Blogs
              </Link>
            </div>
          </article>

          {/* SIDEBAR */}
          <aside style={{ position: "sticky", top: "100px" }}>
            {/* Order CTA Box */}
            <div
              style={{
                background: "#0d0d0d",
                borderRadius: "6px",
                padding: "28px 24px",
                border: "2px solid #f5c518",
                marginBottom: "32px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontFamily: "Raleway, sans-serif",
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                  color: "#f5c518",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <svg viewBox="0 0 100 100" width="14" height="14" fill="currentColor">
                  <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="5"/>
                  <circle cx="50" cy="50" r="38" fill="none" stroke="currentColor" strokeWidth="2"/>
                  <text x="50" y="55" fontSize="26" fontWeight="bold" textAnchor="middle" fill="currentColor">حلال</text>
                  <text x="50" y="75" fontSize="12" fontWeight="bold" textAnchor="middle" fill="currentColor">HALAL</text>
                </svg>
                100% Halal
              </div>
              <p
                style={{
                  fontFamily: "Raleway, sans-serif",
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#fff",
                  lineHeight: 1.4,
                  marginBottom: "20px",
                }}
              >
                Hungry? Order your Seeraga Samba Biryani now!
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <a
                  href="https://www.zomato.com/salem/combo-box-alagapuram-pudur/order"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "block",
                    background: "#cb202d",
                    color: "#fff",
                    fontFamily: "Raleway, sans-serif",
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    padding: "12px",
                    borderRadius: "3px",
                    textAlign: "center",
                    transition: "opacity 0.2s",
                  }}
                >
                  🍽️ Order on Zomato
                </a>
                <a
                  href="https://www.swiggy.com"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "block",
                    background: "#ff5200",
                    color: "#fff",
                    fontFamily: "Raleway, sans-serif",
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    padding: "12px",
                    borderRadius: "3px",
                    textAlign: "center",
                    transition: "opacity 0.2s",
                  }}
                >
                  🛵 Order on Swiggy
                </a>
              </div>
            </div>

            {/* More Posts */}
            <div>
              <h3
                style={{
                  fontFamily: "Raleway, sans-serif",
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                  color: "#1a1a1a",
                  marginBottom: "18px",
                  paddingBottom: "10px",
                  borderBottom: "2px solid #f5c518",
                }}
              >
                More Articles
              </h3>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {otherPosts.map((p) => {
                  const pTagStyle = tagColors[p.tag] || {
                    bg: "#555",
                    color: "#fff",
                  };
                  return (
                    <Link
                      key={p.id}
                      to={`/blog/${p.slug}`}
                      style={{
                        display: "block",
                        background: "#fff",
                        border: "1px solid #e0e0e0",
                        borderRadius: "4px",
                        padding: "16px",
                        textDecoration: "none",
                        transition: "border-color 0.2s, box-shadow 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#f5c518";
                        e.currentTarget.style.boxShadow =
                          "0 4px 16px rgba(0,0,0,0.08)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e0e0e0";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          marginBottom: "10px",
                        }}
                      >
                        {/* Thumbnail */}
                        <div
                          style={{
                            width: "60px",
                            height: "48px",
                            borderRadius: "3px",
                            overflow: "hidden",
                            flexShrink: 0,
                            background: "#0d0d0d",
                          }}
                        >
                          <img
                            src={p.coverImage}
                            alt={p.title}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              objectPosition: "center",
                              display: "block",
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: "8px",
                            fontWeight: 700,
                            letterSpacing: "1.5px",
                            textTransform: "uppercase",
                            padding: "3px 8px",
                            borderRadius: "2px",
                            background: pTagStyle.bg,
                            color: pTagStyle.color,
                            fontFamily: "Raleway, sans-serif",
                          }}
                        >
                          {p.tag}
                        </span>
                      </div>
                      <p
                        style={{
                          fontFamily: "Raleway, sans-serif",
                          fontSize: "13px",
                          fontWeight: 700,
                          color: "#1a1a1a",
                          lineHeight: 1.4,
                          marginBottom: "6px",
                        }}
                      >
                        {p.title}
                      </p>
                      <p
                        style={{
                          fontFamily: "Lato, sans-serif",
                          fontSize: "11px",
                          color: "#888",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {p.date} · {p.readTime}
                      </p>
                    </Link>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      </main>

      <Footer />

      {/* Responsive */}
      <style>{`
        @media (max-width: 900px) {
          .blog-detail-grid {
            grid-template-columns: 1fr !important;
          }
          .blog-detail-grid aside {
            position: static !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BlogDetailPage;
